import React, { useState } from 'react';
import { University } from '../../types';
import './CollegeCard.css';

interface CollegeCardProps {
  university: University;
  onFavoriteClick?: (universityId: number) => void;
  isUpdatingFavorite?: boolean;
  onClick?: () => void;
}

const CollegeCard: React.FC<CollegeCardProps> = ({ 
  university, 
  onFavoriteClick,
  isUpdatingFavorite = false,
  onClick
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleHeartClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when heart is clicked
    if (isUpdatingFavorite) return;
    
    // Trigger animation
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 600); // Animation duration
    
    // Call the actual click handler
    onFavoriteClick?.(university.id);
  };

  const handleCardClick = () => {
    onClick?.();
  };
  const getApplicationTag = (acceptanceRate: number) => {
    if (acceptanceRate < 20) {
      return { label: 'Reach', className: 'bg-orange-100 text-orange-800' };
    } else if (acceptanceRate < 40) {
      return { label: 'Target', className: 'bg-blue-100 text-blue-800' };
    } else {
      return { label: 'Safety', className: 'bg-green-100 text-green-800' };
    }
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return num.toLocaleString();
  };

  const formatStudentCount = (count: number) => {
    if (count >= 1000) {
      return `${Math.round(count / 1000)}k`;
    }
    return count.toString();
  };

  const getPrivatePublic = () => {
    // This would need to be determined from university data
    // For now, we'll use a simple heuristic based on tuition
    if (university.in_state_tuition && university.out_of_state_tuition) {
      return university.in_state_tuition < university.out_of_state_tuition ? 'Public' : 'Private';
    }
    return 'Private'; // Default assumption
  };

  const tag = getApplicationTag(university.acceptance_rate);
  const avgSat = university.sat_range_min && university.sat_range_max 
    ? Math.round((university.sat_range_min + university.sat_range_max) / 2)
    : null;
  const avgAct = university.act_range_min && university.act_range_max 
    ? Math.round((university.act_range_min + university.act_range_max) / 2)
    : null;

  return (
    <div 
      className="bg-white dark:bg-gray-800 border border-[#e5e7eb] dark:border-gray-700 rounded-xl flex p-6 items-center space-x-6 relative cursor-pointer hover:shadow-lg transition-shadow duration-200"
      onClick={handleCardClick}
    >
      {/* Placeholder for image - skipped as requested */}
      <div className="rounded-lg w-[120px] h-[90px] bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 dark:text-gray-500 text-lg mb-1">
            <i className="far fa-image"></i>
          </div>
          <div className="text-gray-400 dark:text-gray-500 text-xs font-medium">No Image</div>
        </div>
      </div>
      
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-bold text-xl text-black dark:text-gray-100">
              {university.name}
            </span>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {Math.round(university.acceptance_rate)}% | {formatStudentCount(university.undergrad_students)} | {getPrivatePublic()} | {university.location}
            </div>
          </div>
          <button 
            data-tour="heart-icon"
            className={`text-xl heart-button ${
              isUpdatingFavorite 
                ? 'updating text-gray-300' 
                : university.in_my_list 
                  ? 'favorited' 
                  : 'unfavorited'
            } ${isAnimating ? 'animate-heartPop' : ''}`}
            onClick={handleHeartClick}
            disabled={isUpdatingFavorite}
          >
            {isUpdatingFavorite ? (
              <i className="fas fa-spinner fa-spin"></i>
            ) : (
              <i className={`${university.in_my_list ? 'fas fa-heart' : 'far fa-heart'} ${
                university.in_my_list ? 'heart-favorited' : ''
              } ${
                isAnimating && university.in_my_list ? 'animate-heartBeat animate-heartGlow' : ''
              } ${
                isAnimating && !university.in_my_list ? 'animate-heartBeat' : ''
              }`}></i>
            )}
          </button>
        </div>
        
        <div className="flex items-center mt-2">
          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${tag.className}`}>
            {tag.label}
          </span>
        </div>
        
        <div className="flex items-center space-x-12 mt-4" data-tour="admissions-stats">
          <div>
            <div className="font-bold text-2xl text-black dark:text-gray-100">
              {avgSat || 'N/A'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Average SAT
            </div>
          </div>
          <div>
            <div className="font-bold text-2xl text-black dark:text-gray-100">
              {avgAct || 'N/A'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Average ACT
            </div>
          </div>
          <div>
            <div className="font-bold text-2xl text-black dark:text-gray-100">
              {Math.round(university.acceptance_rate)}%
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Acceptance Rate
            </div>
          </div>
          <div className="ml-auto">
            <a 
              className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline cursor-pointer" 
              onClick={() => {/* TODO: Implement calculate chances */}}
            >
              Calculate my chances
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollegeCard;
